import { useRef, useState, useEffect } from "react";
// import Product3DShowcase from './Product3DShowcase';
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useScrollProgress } from "./hooks/useScrollProgress";
import StadiumFloodlights from "./StadiumFloodlights";

interface Feature {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface StickyFeatureSectionProps {
  productImage?: string;
  productImageAlt?: string;
  features: Feature[];
  title?: string;
  use3D?: boolean;
  productImages?: string[];
  customComponents?: React.ReactNode[];
  mode?: "scroll" | "slideshow";
  autoPlay?: boolean;
  autoPlayInterval?: number;
  id?: string;
}

const StickyFeatureSection = ({
  productImage,
  productImageAlt,
  features,
  title,
  use3D = false,
  productImages = [],
  customComponents = [],
  mode = "scroll",
  autoPlay = false,
  autoPlayInterval = 5000,
  id,
}: StickyFeatureSectionProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const progress = useScrollProgress(sectionRef);

  // State for slideshow mode
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Auto-play functionality for slideshow mode
  useEffect(() => {
    if (mode === "slideshow" && autoPlay) {
      const interval = setInterval(() => {
        setSlideshowIndex((prev) => (prev + 1) % features.length);
      }, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [mode, autoPlay, autoPlayInterval, features.length]);

  // Minimum swipe distance (in px) to trigger slide change
  const minSwipeDistance = 50;

  // Touch event handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && slideshowIndex < features.length - 1) {
      handleNext();
    }
    if (isRightSwipe && slideshowIndex > 0) {
      handlePrev();
    }
  };

  // Calculate which feature should be highlighted
  const activeIndex =
    mode === "slideshow"
      ? slideshowIndex
      : Math.min(Math.floor(progress * features.length), features.length - 1);

  // Dynamic scroll height based on number of features - capped at 140vh
  const scrollHeight = Math.min(140, Math.max(100, 60 + features.length * 15));

  // Navigation handlers for slideshow
  const handlePrev = () => {
    setSlideshowIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setSlideshowIndex((prev) => Math.min(features.length - 1, prev + 1));
  };

  // Render slideshow mode
  if (mode === "slideshow") {
    return (
      <section
        ref={sectionRef}
        id={id}
        className="relative py-24 scroll-mt-24 bg-gradient-to-b from-background via-muted/10 to-background"
      >
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center max-w-7xl mx-auto">
            {/* Image Carousel - Left Side */}
            <div className="relative">
              <div
                className="relative h-[500px] lg:h-[600px] flex items-center justify-center"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {customComponents[activeIndex] ? (
                  <div
                    className={`absolute inset-0 m-auto flex items-center justify-center transition-all duration-700 ease-out ${
                      "opacity-100 scale-100"
                    }`}
                  >
                    {customComponents[activeIndex]}
                  </div>
                ) : null}

                {productImages.length > 0 ? (
                  <>
                    {productImages.map((image, index) => {
                      if (customComponents[index]) return null;

                      return (
                        <img
                          key={index}
                          src={image}
                          alt={`Feature ${index + 1}`}
                          className={`absolute inset-0 m-auto max-w-full max-h-full object-contain rounded-2xl shadow-premium-xl transition-all duration-700 ease-out ${
                            index === activeIndex
                              ? "opacity-100 scale-100"
                              : "opacity-0 scale-95 pointer-events-none"
                          }`}
                        />
                      );
                    })}

                    {/* Navigation Arrows */}
                    <button
                      onClick={handlePrev}
                      disabled={slideshowIndex === 0}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-background/80 backdrop-blur-sm border border-border hover:bg-accent transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10 flex items-center justify-center group"
                      aria-label="Previous slide"
                    >
                      <ChevronLeft className="w-6 h-6 text-foreground group-hover:text-accent-foreground transition-colors" />
                    </button>

                    <button
                      onClick={handleNext}
                      disabled={slideshowIndex === features.length - 1}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-background/80 backdrop-blur-sm border border-border hover:bg-accent transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10 flex items-center justify-center group"
                      aria-label="Next slide"
                    >
                      <ChevronRight className="w-6 h-6 text-foreground group-hover:text-accent-foreground transition-colors" />
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            {/* Content - Right Side */}
            <div className="relative">
              {title && (
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-12 leading-[1.1] tracking-[-0.02em]">
                  {title}
                </h2>
              )}

              {/* Single feature container - absolute positioning */}
              <div className="relative min-h-[280px]">
                {features.map((feature, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <div
                      key={index}
                      className={`absolute top-0 left-0 w-full transition-all duration-700 ease-expo ${
                        isActive
                          ? "opacity-100 translate-y-0 z-10"
                          : "opacity-0 translate-y-8 z-0 pointer-events-none"
                      }`}
                    >
                      {feature.icon && (
                        <div className="text-accent mb-6">{feature.icon}</div>
                      )}
                      <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-5 leading-[1.2]">
                        {feature.title}
                      </h3>
                      <p className="text-lg md:text-xl text-muted-foreground leading-[1.6]">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Indicator Dots */}
              <div className="flex gap-3 justify-start mt-12">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setSlideshowIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === activeIndex
                        ? "bg-accent w-8"
                        : "bg-muted-foreground/30 w-2 hover:bg-muted-foreground/50"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Render scroll mode (default)
  return (
    <section
      ref={sectionRef}
      id={id}
      className="relative"
      style={{ minHeight: `${scrollHeight}vh` }}
    >
      {/* Stadium atmosphere for 3D showcase */}
      <StadiumFloodlights />

      {/* Background gradient for visual continuity */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/10 to-background -z-10" />

      {/* Stadium lighting atmosphere */}
      <div className="absolute inset-0 stadium-spotlight -z-5" />

      {/* Pitch horizon gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-64 pitch-horizon -z-5" />

      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-20 items-center">
            {/* Product Showcase - Left Side (Dominant) */}
            <div className="relative h-[80vh] flex items-center justify-center">
              {use3D ? (
                // <Product3DShowcase
                //   rotationProgress={progress}
                //   activeFeatureIndex={activeIndex}
                //   productImages={productImages}
                // />
                <h1>3D Feature will be added later</h1>
              ) : productImages.length > 0 ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  {productImages.map((image, index) => {
                    const isActive = index === activeIndex;
                    const custom = customComponents[index];

                    return (
                      <div
                        key={index}
                        className={`absolute inset-0 m-auto transition-all duration-700 ease-out ${
                          isActive
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-95 pointer-events-none"
                        }`}
                      >
                        {custom ? (
                          <div className="w-full h-full flex items-center justify-center">
                            {custom}
                          </div>
                        ) : (
                          <img
                            src={image}
                            alt={`Product view ${index + 1}`}
                            className="max-w-full max-h-full object-contain rounded-2xl shadow-premium-xl"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={productImage}
                    alt={productImageAlt}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain rounded-2xl shadow-premium-xl"
                  />
                </div>
              )}
            </div>

            {/* Features Text - Right Side */}
            <div className="relative h-[80vh] flex items-center">
              <div className="relative w-full">
                {title && (
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-16 leading-[1.1] tracking-[-0.02em]">
                    {title}
                  </h2>
                )}

                {/* Single feature container - absolute positioning */}
                <div className="relative min-h-[200px]">
                  {features.map((feature, index) => {
                    const isActive = index === activeIndex;

                    return (
                      <div
                        key={index}
                        className={`absolute top-0 left-0 w-full transition-all duration-1000 ease-expo ${
                          isActive
                            ? "opacity-100 translate-y-0 z-10"
                            : "opacity-0 translate-y-8 z-0 pointer-events-none"
                        }`}
                      >
                        {feature.icon && (
                          <div className="text-accent mb-6">{feature.icon}</div>
                        )}
                        <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-5 leading-[1.2]">
                          {feature.title}
                        </h3>
                        <p className="text-lg md:text-xl text-muted-foreground leading-[1.6]">
                          {feature.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StickyFeatureSection;
